/**
 * AI Practice Test service.
 *
 * Calls the existing `generate-questions` edge function in "generate-only"
 * mode (`returnAllQuestions: true`, no `saveOnlyQuestions`) to produce a
 * fresh set of multiple-choice questions on demand. The questions are NOT
 * persisted to the curated `questions` bank — they live only in the
 * client-side test session.
 *
 * Guests are subject to the shared 5-per-6h AI generation cap; the
 * `invokeAI` wrapper attaches `x-guest-id` and surfaces a friendly toast on
 * 429 with a "Sign up free" CTA. Signed-in users bypass the cap.
 */

import { invokeAI, AIRateLimitError } from "@/lib/aiInvoke";

export interface GeneratedTestQuestion {
  id: string;
  text: string;
  explanation?: string;
  options: Array<{
    id: string;
    text: string;
    is_correct: boolean;
  }>;
}

export interface GeneratePracticeTestParams {
  grade: string;
  subjectId: string;
  categoryId?: string | null;
  topicTitle?: string | null;
  focusHint?: string | null;
  count: number;
  difficulty: "easy" | "medium" | "hard";
}

interface RawAIQuestion {
  text: string;
  explanation?: string;
  isDuplicate?: boolean;
  options: Array<{ text: string; isCorrect?: boolean; is_correct?: boolean }>;
}

interface RawAIResponse {
  questions?: RawAIQuestion[];
}

function buildPrompt(p: GeneratePracticeTestParams): string | undefined {
  const bits: string[] = [];
  if (p.topicTitle) bits.push(`Focus the questions on the topic: "${p.topicTitle}".`);
  if (p.focusHint) bits.push(`Additional focus: ${p.focusHint}.`);
  if (!bits.length) return undefined;
  return bits.join(" ");
}

export async function generatePracticeTest(
  params: GeneratePracticeTestParams,
): Promise<GeneratedTestQuestion[]> {
  const customPrompt = buildPrompt(params);

  const { data, error } = await invokeAI<RawAIResponse>("generate-questions", {
    body: {
      grade: params.grade,
      subject_id: params.subjectId,
      category_id: params.categoryId ?? null,
      count: Math.max(1, Math.min(20, params.count)),
      difficulty: params.difficulty,
      returnAllQuestions: true,
      generateWithTopic: false,
      ...(customPrompt ? { customPrompt } : {}),
    },
  });

  if (error) {
    if (error instanceof AIRateLimitError) throw error;
    throw new Error(error?.message ?? "Failed to generate practice test");
  }

  const raw = data?.questions ?? [];
  if (!raw.length) {
    throw new Error("The AI did not return any questions. Please try again.");
  }

  // Normalize to the shape TestInterface expects, with stable client IDs.
  return raw.map((q) => {
    const qid = crypto.randomUUID();
    return {
      id: qid,
      text: q.text,
      explanation: q.explanation,
      options: (q.options ?? []).map((o) => ({
        id: crypto.randomUUID(),
        text: o.text,
        is_correct: o.is_correct ?? o.isCorrect ?? false,
      })),
    };
  });
}
